import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Star } from "lucide-react";
import { RingFull } from "./GeoShapes";

const REVIEWS = [
  {
    user: "tombacco_1919",
    country: "Italia",
    gig: "Label Design",
    accent: "#E200C8",
    text: "Ha capito subito le mie esigenze e le ha tradotte in un risultato creativo, pulito e professionale. Comunicazione chiara e tempi rispettati. Consiglio sicuramente a chi cerca qualcuno affidabile e competente!",
  },
  {
    user: "darkkee",
    country: "Italia",
    gig: "Website UI/UX",
    accent: "#00C2E8",
    text: "Beppe is really a good designer, for sure we'll work another time together!",
  },
  {
    user: "evans_agua",
    country: "Stati Uniti",
    gig: "Icon Design",
    accent: "#2020E0",
    text: "One of the best we have ever worked with.",
  },
  {
    user: "nicolaonida",
    country: "Italia",
    gig: "Logo Design",
    accent: "#E200C8",
    text: "Beppe ha capito immediatamente le mie richieste ed è stato bravissimo a trovare una soluzione fedele a ciò che cercavo. Ha lavorato con estrema rapidità.",
  },
  {
    user: "sunshiyi",
    country: "Cina",
    gig: "Logo Design",
    accent: "#00C2E8",
    text: "Nice job!",
  },
];

const AUTOPLAY_MS = 4000;

const Stars = () => (
  <span className="flex gap-0.5" aria-label="5 stelle su 5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className="h-3.5 w-3.5 fill-current text-[#FFB33E]" strokeWidth={0} />
    ))}
  </span>
);

export default function ReviewsSection() {
  const trackRef = useRef(null);
  const [paused, setPaused] = useState(false);

  const getStep = () => {
    const track = trackRef.current;
    if (!track) return 380;
    const card = track.querySelector("[data-review-card]");
    return card ? card.offsetWidth + 1 : 380;
  };

  const scrollByCard = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: dir * getStep(), behavior: "smooth" });
  };

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      const track = trackRef.current;
      if (!track) return;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 10;
      track.scrollTo({ left: atEnd ? 0 : track.scrollLeft + getStep(), behavior: "smooth" });
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <section id="recensioni" data-testid="reviews-section" className="relative">
      <RingFull color="#00C2E8" className="pointer-events-none absolute right-8 top-20 hidden h-14 w-14 lg:block" />
      <div className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 lg:py-24">
        <div className="mb-12 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <span className="font-mono text-xs font-bold tracking-[0.25em] text-cyan-brand">
              SOCIAL PROOF
            </span>
            <h2
              data-testid="reviews-heading"
              className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl"
            >
              Dicono di me su Fiverr
            </h2>
          </div>
          <div className="flex items-center gap-4" data-testid="reviews-summary">
            <span className="font-display text-5xl font-bold tracking-tighter">5.0</span>
            <div>
              <Stars />
              <p className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-neutral-500">
                16 recensioni · Level 1 Seller
              </p>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div
            ref={trackRef}
            data-testid="reviews-track"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={() => setPaused(true)}
            onTouchEnd={() => setPaused(false)}
            className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-px overflow-x-auto border-y border-neutral-200 bg-neutral-200 px-5 sm:-mx-8 sm:px-8"
          >
            {REVIEWS.map((r) => (
              <figure
                key={r.user}
                data-review-card
                data-testid={`review-card-${r.user}`}
                className="relative flex w-[85%] shrink-0 snap-start flex-col border-x border-neutral-200 bg-white p-7 sm:w-[380px]"
              >
                <span
                  className="absolute left-0 top-0 h-1 w-full"
                  style={{ backgroundColor: r.accent }}
                />
                <Stars />
                <blockquote className="mt-5 flex-1 text-sm leading-relaxed text-neutral-600">
                  “{r.text}”
                </blockquote>
                <figcaption className="mt-6 flex items-center justify-between border-t border-neutral-100 pt-4">
                  <div>
                    <span className="block font-display text-sm font-bold tracking-tight">
                      {r.user}
                    </span>
                    <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-wider text-neutral-400">
                      {r.country}
                    </span>
                  </div>
                  <span
                    className="px-2.5 py-1 font-mono text-[10px] tracking-wider text-white"
                    style={{ backgroundColor: r.accent }}
                  >
                    {r.gig}
                  </span>
                </figcaption>
              </figure>
            ))}

            <a
              href="https://www.fiverr.com/peppefasino"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-fiverr-link"
              className="group relative flex w-[70%] shrink-0 snap-start flex-col items-start justify-between border-x border-neutral-200 bg-ink p-7 text-paper transition-colors duration-300 hover:bg-blue-brand sm:w-[320px]"
            >
              <span className="font-display text-2xl font-bold leading-tight tracking-tight">
                Altre 11
                <br />
                recensioni
                <br />
                ti aspettano
              </span>
              <span className="mt-8 flex items-center gap-2 font-mono text-xs tracking-wider">
                VAI AL PROFILO FIVERR
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </span>
            </a>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-wider text-neutral-400">
              {paused ? "In pausa — riprende quando esci" : "Scorrimento automatico attivo"}
            </span>
            <div className="flex gap-2">
              <button
                data-testid="reviews-prev-button"
                onClick={() => scrollByCard(-1)}
                aria-label="Recensione precedente"
                className="flex h-11 w-11 items-center justify-center border border-neutral-300 transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-paper"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button
                data-testid="reviews-next-button"
                onClick={() => scrollByCard(1)}
                aria-label="Recensione successiva"
                className="flex h-11 w-11 items-center justify-center border border-neutral-300 transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-paper"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
